import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SERVICES } from "../data/site";

export function ServicesList() {
  return (
    <div data-testid="services-page">
      <section className="bg-brand-blue text-white py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="eyebrow text-brand-yellow mb-6">Services</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">All Things Social Impact is a specialist social impact and social risk advisory practice led by Andrea Kanaris.</h1>
          <p className="text-lg lg:text-xl text-white/80 max-w-3xl mt-6 leading-relaxed">Ten senior-level advisory offerings, from early-stage social risk strategy to independent peer review - all led personally by Andrea Kanaris.</p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s, i) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="block bg-white border border-slate-200 p-8 hover:border-brand-orange hover:shadow-md transition-all">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-orange font-semibold mb-4">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-serif text-xl text-brand-blue leading-snug mb-3 min-h-[3.5rem]">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-5">{s.short}</p>
                <span className="text-sm font-semibold text-brand-blue inline-flex items-center gap-2">Learn more <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return (
    <div className="py-32 text-center">
      <h1 className="font-serif text-3xl text-brand-blue mb-4">Service not found</h1>
      <Link to="/services" className="link-arrow">View all services <ArrowRight className="w-4 h-4" /></Link>
    </div>
  );

  return (
    <div data-testid={`service-detail-${slug}`}>
      <section className="bg-brand-blue text-white py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to="/services" className="text-brand-yellow text-xs uppercase tracking-[0.2em] font-semibold hover:underline">← All services</Link>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mt-6 max-w-4xl">{service.title}</h1>
          <p className="text-lg text-white/80 max-w-3xl mt-5">{service.short}</p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">The advisory</div>
            <p className="text-slate-700 text-lg leading-relaxed mb-8">{service.body}</p>

            <div className="eyebrow mb-4 mt-10">Typical outcomes</div>
            <div className="space-y-3">
              {service.outcomes.map((o) => (
                <div key={o} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" />
                  <div className="text-slate-700">{o}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-slate-50 border border-slate-200 p-8 sticky top-28">
              <div className="eyebrow mb-4">Ready to discuss?</div>
              <h3 className="font-serif text-2xl text-brand-blue leading-snug mb-4">Tell us about your project.</h3>
              <p className="text-slate-600 text-sm mb-6">A short inquiry gives us enough context to come back with a considered response - usually within two business days.</p>
              <button onClick={() => navigate("/contact")} className="btn-primary w-full justify-center" data-testid={`service-cta-${slug}`}>Contact Us <ArrowRight className="w-4 h-4" /></button>
              <div className="mt-6 pt-6 border-t border-slate-200">
                <div className="eyebrow mb-3">Related services</div>
                <div className="space-y-2">
                  {SERVICES.filter((s) => s.slug !== slug).slice(0, 3).map((s) => (
                    <Link key={s.slug} to={`/services/${s.slug}`} className="block text-sm text-brand-blue hover:text-brand-orange">{s.title} →</Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
